<template>
  <div class="search">
    <!-- The trigger is a button, not an input: the real search happens in the
         palette, which needs the whole viewport to show grouped results. -->
    <button type="button" class="search__trigger" @click="open()">
      <AppIcon name="search" :size="16" />
      <span class="search__placeholder">{{ t('search.placeholder') }}</span>
      <kbd class="search__kbd">{{ shortcutLabel }}</kbd>
    </button>

    <Teleport to="body">
      <Transition name="fade">
        <div v-if="isOpen" class="palette" role="dialog" aria-modal="true" @mousedown.self="close">
          <Transition name="slide-up" appear>
            <div class="palette__panel">
              <div class="palette__input-row">
                <AppIcon name="search" :size="18" />
                <input
                  ref="input"
                  v-model="term"
                  class="palette__input"
                  type="text"
                  :placeholder="t('search.placeholder')"
                  :aria-label="t('search.placeholder')"
                  autocomplete="off"
                  spellcheck="false"
                  @keydown.down.prevent="move(1)"
                  @keydown.up.prevent="move(-1)"
                  @keydown.enter.prevent="choose(flat[cursor])"
                  @keydown.esc="close"
                />
                <span v-if="loading" class="palette__spinner" aria-hidden="true" />
              </div>

              <div v-if="flat.length" class="palette__results">
                <section v-for="group in groups" :key="group.id" class="palette__group">
                  <p class="palette__group-title">{{ groupLabel(group) }}</p>
                  <button
                    v-for="entry in group.results"
                    :key="`${group.id}:${entry.id}`"
                    type="button"
                    class="palette__item"
                    :class="{ 'palette__item--active': flat[cursor] === entry }"
                    @click="choose(entry)"
                    @mousemove="cursor = flat.indexOf(entry)"
                  >
                    <span class="grow">
                      <span class="palette__title">{{ entry.title }}</span>
                      <span v-if="entry.subtitle" class="palette__subtitle">{{ entry.subtitle }}</span>
                    </span>
                    <UiBadge v-if="entry.badge" variant="neutral">{{ entry.badge }}</UiBadge>
                  </button>
                </section>
              </div>

              <p v-else-if="term.length >= 2 && !loading" class="palette__empty">
                {{ t('search.noResults', { term }) }}
              </p>

              <p v-else class="palette__hint">{{ t('search.hint') }}</p>

              <footer class="palette__foot">
                <span><kbd>↑</kbd><kbd>↓</kbd> {{ t('search.navigate') }}</span>
                <span><kbd>↵</kbd> {{ t('search.select') }}</span>
                <span><kbd>esc</kbd> {{ t('search.close') }}</span>
              </footer>
            </div>
          </Transition>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { api, UiBadge } from '@general/ui-kit'
import { useAuthStore } from '@general/auth/frontend'

import AppIcon from './AppIcon.vue'
import { visibleNavigation } from '../app/navigation'

const DEBOUNCE_MS = 200
const MIN_LENGTH = 2

const { t } = useI18n()
const router = useRouter()
const auth = useAuthStore()

const isOpen = ref(false)
const term = ref('')
const cursor = ref(0)
const loading = ref(false)
const remoteGroups = ref([])

const input = ref(null)
let debounce = null
let requestId = 0

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform ?? '')
const shortcutLabel = isMac ? '⌘K' : 'Ctrl K'

// Sections of the app itself, matched locally: navigating to a screen is the
// most common thing anyone types, and it should not wait for the network.
const sectionMatches = computed(() => {
  const needle = term.value.toLowerCase()
  if (needle.length < MIN_LENGTH) return []

  return visibleNavigation(auth.can)
    .flatMap(group => group.items)
    .map(item => ({ id: item.to, title: t(item.labelKey), route: item.to, newTab: item.newTab }))
    .filter(entry => entry.title.toLowerCase().includes(needle))
})

const groups = computed(() => {
  const local = sectionMatches.value.length
    ? [{ id: 'sections', label: 'search.sections', results: sectionMatches.value }]
    : []
  return [...local, ...remoteGroups.value]
})

// Flattened, in display order: that is what the arrow keys walk.
const flat = computed(() => groups.value.flatMap(group => group.results))

const groupLabel = (group) => {
  const translated = t(group.label)
  return translated === group.label ? group.label : translated
}

async function runSearch() {
  const query = term.value.trim()
  if (query.length < MIN_LENGTH) {
    remoteGroups.value = []
    return
  }

  const id = ++requestId
  loading.value = true
  try {
    const data = await api.get(`/search?q=${encodeURIComponent(query)}`)
    // Out-of-order responses: a slow early request must not overwrite the
    // results of a later, faster one.
    if (id === requestId) remoteGroups.value = data.groups
  } catch {
    if (id === requestId) remoteGroups.value = []
  } finally {
    if (id === requestId) loading.value = false
  }
}

watch(term, () => {
  cursor.value = 0
  clearTimeout(debounce)
  debounce = setTimeout(runSearch, DEBOUNCE_MS)
})

function move(delta) {
  if (!flat.value.length) return
  const next = cursor.value + delta
  // Wraps around, so holding a key never leaves you stuck at an end.
  cursor.value = (next + flat.value.length) % flat.value.length
}

function choose(entry) {
  if (!entry) return
  close()
  // Lo que el menú abre en otra pestaña (la documentación) se abre igual desde
  // aquí: el mismo destino no puede comportarse distinto según por dónde se llegue.
  if (entry.newTab) window.open(router.resolve(entry.route).href, '_blank', 'noopener')
  else router.push(entry.route)
}

async function open() {
  isOpen.value = true
  await nextTick()
  input.value?.focus()
}

function close() {
  isOpen.value = false
  term.value = ''
  remoteGroups.value = []
  cursor.value = 0
}

function onKeydown(event) {
  if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
    event.preventDefault()
    isOpen.value ? close() : open()
  }
}

onMounted(() => document.addEventListener('keydown', onKeydown))
onUnmounted(() => {
  document.removeEventListener('keydown', onKeydown)
  clearTimeout(debounce)
})
</script>

<style scoped>
.search__trigger {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  height: 36px;
  min-width: 220px;
  padding: 0 var(--space-2) 0 var(--space-3);
  border: 1px solid var(--color-border);
  border-radius: var(--radius);
  background: var(--color-surface-2);
  color: var(--color-text-mute);
  font-size: var(--text-sm);
}
.search__trigger:hover { border-color: var(--color-primary); color: var(--color-text-soft); }

/* ── Con el dedo ────────────────────────────────────────────────────────────
   El buscador global medía 40x36 en un móvil, donde se encoge a icono. Es la
   entrada más rápida que hay a cualquier ficha del ERP.
   `pointer: coarse` y no un ancho de pantalla: lo que decide es con qué se
   toca — una tableta con teclado tiene pantalla de sobra y se sigue tocando
   con el dedo. */
@media (pointer: coarse) {
  .search__trigger { height: 44px; }
}


.search__placeholder { flex: 1; text-align: left; }

.search__kbd {
  padding: 2px 6px;
  border: 1px solid var(--color-border);
  border-radius: 4px;
  background: var(--color-surface);
  font-family: var(--font);
  font-size: 10px;
  font-weight: 600;
}

/* ── Palette ─────────────────────────────────────────────────────────────── */
.palette {
  position: fixed;
  inset: 0;
  /* Lo que se ve (ui-kit utils/viewport.js): se escribe nada más abrir, y con
     el teclado fuera en iOS la lista de resultados seguía debajo de él. */
  top: var(--viewport-top, 0px);
  bottom: auto;
  height: var(--app-height, 100dvh);
  z-index: 150;
  display: flex;
  justify-content: center;
  padding: 12vh var(--space-4) var(--space-4);
  background: rgba(15, 23, 42, 0.45);
  backdrop-filter: blur(2px);
}

.palette__panel {
  width: 100%;
  max-width: 620px;
  /* 70vh de la ventana, salvo que lo que se ve (teclado de una tableta) sea menos. */
  max-height: min(70vh, calc(var(--app-height, 100dvh) - 12vh - var(--space-4)));
  display: flex;
  flex-direction: column;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
  overflow: hidden;
}

.palette__input-row {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-4);
  border-bottom: 1px solid var(--color-border);
  color: var(--color-text-mute);
}

.palette__input {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: var(--text-lg);
  color: var(--color-text);
}

.palette__spinner {
  width: 14px; height: 14px;
  border: 2px solid var(--color-border);
  border-top-color: var(--color-primary);
  border-radius: 50%;
  animation: palette-spin 0.6s linear infinite;
}
@keyframes palette-spin { to { transform: rotate(360deg); } }

.palette__results { overflow-y: auto; padding: var(--space-2); }

.palette__group + .palette__group { margin-top: var(--space-3); }

.palette__group-title {
  padding: var(--space-2) var(--space-3);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--color-text-mute);
}

.palette__item {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  width: 100%;
  padding: var(--space-3);
  border: none;
  border-radius: var(--radius-sm);
  background: none;
  text-align: left;
  color: var(--color-text);
}
.palette__item--active { background: var(--color-primary-soft); }

.palette__title { display: block; font-size: var(--text-sm); font-weight: 550; }
.palette__subtitle { display: block; font-size: var(--text-xs); color: var(--color-text-mute); }

.palette__empty,
.palette__hint {
  padding: var(--space-8) var(--space-4);
  text-align: center;
  font-size: var(--text-sm);
  color: var(--color-text-mute);
}

.palette__foot {
  display: flex;
  gap: var(--space-4);
  padding: var(--space-2) var(--space-4);
  border-top: 1px solid var(--color-border);
  background: var(--color-surface-2);
  font-size: var(--text-xs);
  color: var(--color-text-mute);
}
.palette__foot kbd {
  padding: 1px 5px;
  margin-right: 3px;
  border: 1px solid var(--color-border);
  border-radius: 3px;
  background: var(--color-surface);
  font-family: var(--font);
}

@media (max-width: 700px) {
  .search__trigger { min-width: 0; width: 40px; justify-content: center; padding: 0; }
  .search__placeholder,
  .search__kbd { display: none; }

  /*
    The palette takes the screen. 12vh of dimmed backdrop above it is a luxury
    of a large window; on a phone the keyboard is about to eat the bottom half,
    so every pixel goes to the results.
  */
  .palette { padding: 0; }
  .palette__panel {
    max-width: none;
    max-height: 100%;
    height: 100%;
    border: none;
    border-radius: 0;
  }
  .palette__input-row { padding-top: calc(var(--space-4) + env(safe-area-inset-top)); }
  /* The shortcut legend is for a keyboard that is not there. */
  .palette__foot { display: none; }
}
</style>
